import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLocationDot, faHeart } from "@fortawesome/pro-solid-svg-icons"
import { Card, CardActionArea, IconButton, Stack, Typography } from "@mui/material"
import { faHeart as faHeartRegular, faCircleInfo } from "@fortawesome/pro-regular-svg-icons"
import { MeetingServicePriceListCategory, MeetingServicePriceListsItem } from "./priceListsTypes"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { SelectedPriceListItem } from "../../../createOrderTypes"
import { SearchableText } from "../../../../search/Searchable"

const useStyles = makeStyles()((theme) => ({
    cardWrap: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(15rem, 1fr))",
        gap: "1rem",
    },
    cardContent: {
        height: "100%",
        minHeight: "13.75rem",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        padding: "2.5rem 1rem 2.75rem",
    },
    favoriteCardContent: {
        borderRadius: "1rem",
    },
    location: {
        color: theme.palette.tailwind.slate[500],
    },
    priceListName: {
        textAlign: "center",
    },
    favoriteButton: {
        position: "absolute",
        right: ".25rem",
        top: ".25rem",
    },

    iconDark: {
        color: theme.palette.tailwind.slate[950],
    },
    infoWrap: {
        padding: "0 .5rem",
        textAlign: "center",
        color: theme.palette.tailwind.slate[600],
    },
    icon: {
        color: theme.palette.tailwind.slate[400],
        transition: "all 0.3s ease-out",
        display: "inline",
        paddingLeft: ".5rem",
        "&:hover": {
            color: theme.palette.tailwind.slate[500],
        },
    },
    chip: {
        textAlign: "center",
        padding: ".5rem",
        borderRadius: ".5rem",
        display: "inline-block",
        margin: "0 auto",
    },
}))

type SelectPriceListsCardProps = Readonly<{
    onClose: () => void
    merchant: MeetingServicePriceListsItem
    onSelect: (item: SelectedPriceListItem) => void
    searchRegex: RegExp | undefined
    handleFavoriteClick: (merchantId: number, priceListId: number) => void
}>

export function SelectPriceListsCard({
    merchant,
    onSelect,
    searchRegex,
    onClose,
    handleFavoriteClick,
}: SelectPriceListsCardProps) {
    const { classes, cx } = useStyles()

    return (
        <Stack gap="1rem" key={merchant.id}>
            <Stack gap=".5rem">
                <Typography variant="h5">{merchant.displayName}</Typography>
                {merchant?.address?.displayName && (
                    <Stack
                        flexDirection="row"
                        gap=".5rem"
                        alignItems="center"
                        flexWrap="nowrap"
                        className={classes.location}
                    >
                        <FontAwesomeIcon icon={faLocationDot} size="xs" />
                        <Typography variant="body2">{merchant.address.displayName}</Typography>
                    </Stack>
                )}
            </Stack>
            <Stack flexDirection="row" gap="1rem" className={classes.cardWrap}>
                {merchant.priceLists.map((priceList) => (
                    <Card
                        key={`${merchant.id}-${priceList.id}`}
                        variant="outlined"
                        className={cx(priceList.isFavorite && classes.favoriteCardContent)}
                    >
                        <CardActionArea
                            onClick={() => {
                                onSelect({
                                    merchantId: merchant.id,
                                    merchantName: merchant.displayName,
                                    priceListId: priceList.id,
                                    priceListName: priceList.displayName,
                                    shopId: merchant.shop.id,
                                    isOnlyOnePriceList: false,
                                    priceListIncludeVat: priceList.includeVat,
                                })
                                onClose()
                            }}
                            className={classes.cardContent}
                        >
                            <Stack gap=".75rem" flexGrow="1" justifyContent="center">
                                <IconButton
                                    className={classes.favoriteButton}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleFavoriteClick(merchant.id, priceList.id)
                                    }}
                                >
                                    <FontAwesomeIcon icon={priceList.isFavorite ? faHeart : faHeartRegular} />
                                </IconButton>
                                {priceList.category === MeetingServicePriceListCategory.EXTERNAL && (
                                    <LocalizedStrict id="create-order-select-price-list-external-meetings">
                                        <Typography
                                            variant="body2"
                                            className={classes.chip}
                                            sx={{ background: priceList.colorCode || "white" }}
                                        >
                                            External meetings
                                        </Typography>
                                    </LocalizedStrict>
                                )}
                                <SearchableText search={searchRegex}>
                                    <Typography variant="subtitle1" className={classes.priceListName}>
                                        {priceList.displayName}
                                    </Typography>
                                </SearchableText>
                                {priceList.category === MeetingServicePriceListCategory.EXTERNAL && (
                                    <Stack flexDirection="row" gap=".5rem" alignItems="center">
                                        <Typography variant="body1" className={classes.infoWrap}>
                                            <LocalizedStrict id="create-order-select-price-list-external-meetings-description">
                                                <> Select this if you want to order to external</>
                                            </LocalizedStrict>
                                            <FontAwesomeIcon icon={faCircleInfo} className={classes.icon} />
                                        </Typography>
                                    </Stack>
                                )}
                            </Stack>
                        </CardActionArea>
                    </Card>
                ))}
            </Stack>
        </Stack>
    )
}
